import React, { useLayoutEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import useTranslationLgs from '@hooks/i18n/useTranslation';

interface ComponentProps {
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  /** Key handling for callers that submit on Enter (the assistant composer). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /**
   * Grow with the content instead of scrolling inside `rows` lines — `rows` becomes the minimum. Cap it
   * with a `max-h-*` class; past that the field scrolls as usual.
   */
  autoGrow?: boolean;
  className?: string;
  dataTestId?: string;
}

const RingilTextArea: React.FC<ComponentProps> = ({
  value,
  onChange,
  maxLength,
  placeholder,
  rows = 5,
  disabled,
  onKeyDown,
  autoGrow = false,
  className,
  dataTestId,
}) => {
  const { tr } = useTranslationLgs();
  const ref = useRef<HTMLTextAreaElement>(null);

  // Height follows the content: collapse to measure, then set to what the text needs (plus borders, as
  // scrollHeight excludes them). Before paint, so the field never flickers a line short. Width changes
  // re-wrap the text too — a resized panel — hence the observer.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!autoGrow || !el) return;
    const fit = () => {
      // Collapsing to measure resets the scroll of a field already past its max-h — kept, so typing on
      // the last lines does not jump to the top.
      const scrollTop = el.scrollTop;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
      el.scrollTop = scrollTop;
    };
    fit();
    let width = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
    // className and rows change what one line measures (padding, font, minimum) — re-fit on those too,
    // or the height measured under the old styling sticks until the next keystroke.
  }, [autoGrow, value, className, rows]);
  return (
    <div className={'flex flex-col flex-1 gap-1'}>
      <textarea
        ref={ref}
        maxLength={maxLength}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={twMerge(
          "p-2 font-['DM_Sans'] text-base font-normal leading-normal text-content-primary border border-content-quaternary outline-none focus:border-content-brand focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60",
          className,
        )}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        data-testid={dataTestId}
      />
      {maxLength && (
        <div className={'text-[10px] text-content-secondary text-right'}>
          {tr(`RingilTextArea.limit`, `{{actual}} / {{max}} znaků`, {
            actual: value?.length || 0,
            max: maxLength,
          })}
        </div>
      )}
    </div>
  );
};

export default RingilTextArea;
